import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntervalWhen } from 'rooks';
import { isAdmin } from '@/services/authorization.service';
import {
  DatasourceStatus,
  DatasourceStatusIcons,
  datasourceDisplayName,
  datasourcesStatusMessages,
  fetchDatasourcesImmediately,
  filterAndSortDatasources,
  getDatasourceItemsCount,
  getDatasourceStatus,
} from '@/utilities/datasources.utilities';
import { ButtonWithPopover, Icon } from '@seeqdev/qomponents';
import type { DatasourceFilterParameters } from '@/utilities/datasources.utilities';
import type { DatasourceSummaryStatusOutputV1 } from '@seeqdev/api/model/DatasourceSummaryStatusOutputV1';
import type { DatasourcesStatusOutputV1 } from '@seeqdev/api/model/DatasourcesStatusOutputV1';

export const DatasourcesStatus = () => {
  const [popupShown, setPopupShown] = useState(false);
  const { t } = useTranslation();
  const [datasourcesContainer, setDatasourcesContainer] = useState<DatasourcesStatusOutputV1 | null>(null);
  const [fetchErrors, setFetchErrors] = useState(false);

  const fetchDatasources = () => {
    fetchDatasourcesImmediately()
      .then((datasourcesStatus) => {
        setDatasourcesContainer(datasourcesStatus ?? null);
        setFetchErrors(false);
      })
      .catch(() => setFetchErrors(true));
  };

  useEffect(() => {
    if (popupShown) {
      fetchDatasources();
    }
  }, [popupShown]);

  useIntervalWhen(fetchDatasources, 2000, popupShown);

  const emptyFilters: DatasourceFilterParameters = {
    name: '',
    datasourceClass: '',
    datasourceId: '',
    agentName: '',
    status: '',
    metricsTimeRange: '',
    datasourceLabels: [],
  };

  const emptyDatasourceContainer: DatasourcesStatusOutputV1 = {
    connectedConnectionsCount: 0,
    connectedDatasourcesCount: 0,
    totalConnectionsCount: 0,
    totalDatasourcesCount: 0,
    datasources: [],
    agents: [],
  };

  const renderAdministrationLink = () => {
    if (isAdmin()) {
      return (
        <div className="text-center pt7">
          <a href="/administration" target="_blank">
            <span>{t('DATASOURCES.MANAGEMENT')}</span>
          </a>
        </div>
      );
    }
  };

  const renderDatasourcesTray = (dsContainer: DatasourcesStatusOutputV1) => {
    const datasourcesStatuses = new Map();
    _.forEach(dsContainer.datasources, (ds) => datasourcesStatuses.set(ds, getDatasourceStatus(ds)));

    const getDatasourceStatusMessages = (ds: DatasourceSummaryStatusOutputV1) => {
      const status = datasourcesStatuses.get(ds);
      const messagePlaceholder = datasourcesStatusMessages.get(status) as string;
      const args: Record<string, number> = {};
      if (status === DatasourceStatus.Happy || status === DatasourceStatus.Warning) {
        args['count'] = getDatasourceItemsCount(ds);
      }
      return t(messagePlaceholder, args);
    };

    const renderDatasource = (ds: DatasourceSummaryStatusOutputV1) => {
      const status = datasourcesStatuses.get(ds);
      const icon = DatasourceStatusIcons.get(status) as string;
      const message = getDatasourceStatusMessages(ds);
      return (
        <div className="fakeTableRow fakeTableStriped flexColumnContainer flexNoGrowNoShrink" key={ds.id}>
          <div className="width-300 p7 breakWord">{datasourceDisplayName(ds)}</div>
          <div className="p7">
            <Icon icon={icon} type="inherit" />
            <span className="ml5">{message}</span>
          </div>
        </div>
      );
    };

    const renderDatasources = () => {
      const enabledDatasources = _.filter(
        dsContainer.datasources,
        (ds) => getDatasourceStatus(ds) !== DatasourceStatus.NotConnectable,
      );
      const filteredDatasources = filterAndSortDatasources(enabledDatasources, emptyFilters);
      return (
        <div className="flexRowContainer fakeTableWrapper" data-testid="datasourcesList">
          {filteredDatasources ? filteredDatasources.map(renderDatasource) : null}
        </div>
      );
    };

    const renderHeader = () => {
      return (
        <div className="flexColumnContainer fakeTableHeader">
          <div className="width-300 pt7 pr7 pb2 pl7">{t('NAME')}</div>
          <div className="nowrap pt7 pr7 pb2 pl7">{t('STATUS')}</div>
        </div>
      );
    };

    return (
      <ButtonWithPopover
        isOpen={popupShown}
        trigger={
          <div className="ml3" id="toggleDatasources" data-testid="datasourcesStatus">
            <span className="tw:hidden tw:text-sm tw:sm:inline-block!">{t('DATASOURCES.TITLE')}</span>
          </div>
        }
        hasArrow
        tooltip={t('DATASOURCES.TOOLTIP')}
        onOpenChange={(isOpen) => setPopupShown(isOpen)}
        placement="top">
        <div id="dataSourcesPopup" className="sq-text-color min-width-500">
          <div className="flexRowContainer p10">
            {!fetchErrors && dsContainer.totalConnectionsCount !== 0 && renderHeader()}
            {!fetchErrors && dsContainer.totalConnectionsCount !== 0 && renderDatasources()}
            {fetchErrors && (
              <div className="flexColumnContainer sq-text-danger cursorPointer pt3 pb6">
                <Icon icon="fa-exclamation-triangle" type="danger" extraClassNames="flexCenter pl5 pr10 pt2" />
                <div className="flexFill pl5">{t('DATASOURCES.FETCH_ERROR')}</div>
              </div>
            )}
            {renderAdministrationLink()}
          </div>
        </div>
      </ButtonWithPopover>
    );
  };

  return datasourcesContainer
    ? renderDatasourcesTray(datasourcesContainer)
    : renderDatasourcesTray(emptyDatasourceContainer);
};
